import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChevronRight, AlertCircle } from 'lucide-react';
import { selectUser } from '../store/authSlice';
import { commissionReportsApi } from '../api/commissionReportsApi';

const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * List view: pick a year, see one row per month with its status and file
 * counts. Clicking a row navigates to that month's detail page.
 *
 * Backend returns only months that have a run record. The page renders a
 * full 12-month scaffold and overlays the API data on top, so months with
 * no uploads yet still show as a clickable row in NO_DATA state.
 */
export default function CommissionReportsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const orgUuid = user?.organisationUuid;

    const [years, setYears] = useState([]);
    const [yearsLoading, setYearsLoading] = useState(true);
    const [runs, setRuns] = useState([]);
    const [monthsLoading, setMonthsLoading] = useState(false);
    const [error, setError] = useState(null);

    const yearParam = searchParams.get('year');
    const selectedYear = yearParam ? Number(yearParam) : null;

    // Years: load once. Backend returns just the years that have data; if
    // the list is empty, default the picker to the current year so the
    // user has somewhere to upload.
    useEffect(() => {
        if (!orgUuid) return;
        let cancelled = false;
        commissionReportsApi
            .listYears(orgUuid)
            .then((data) => {
                if (cancelled) return;
                const list = data.length > 0 ? data : [new Date().getFullYear()];
                setYears(list);
                if (!yearParam) {
                    setSearchParams({ year: String(list[0]) }, { replace: true });
                }
            })
            .catch((err) => !cancelled && setError(err.message))
            .finally(() => !cancelled && setYearsLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgUuid]);

    // Months: refetch on year change.
    useEffect(() => {
        if (!orgUuid || !selectedYear) return;
        let cancelled = false;
        setMonthsLoading(true);
        setError(null);
        commissionReportsApi
            .listMonths(orgUuid, selectedYear)
            .then((data) => !cancelled && setRuns(data))
            .catch((err) => !cancelled && setError(err.message))
            .finally(() => !cancelled && setMonthsLoading(false));
        return () => {
            cancelled = true;
        };
    }, [orgUuid, selectedYear]);

    // Build the full 12-month scaffold for the selected year, overlaying
    // any runs returned by the API.
    const monthRows = buildMonthRows(selectedYear, runs);

    return (
        <div className="px-8 py-8 max-w-5xl">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h2 className="font-display text-2xl font-semibold text-slate-900">
                        Commission reports
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Monthly commission calculations and their generated reports.
                    </p>
                </div>
                <YearPicker
                    years={years}
                    loading={yearsLoading}
                    selectedYear={selectedYear}
                    onChange={(y) => setSearchParams({ year: String(y) })}
                />
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <Th>Month</Th>
                        <Th>Status</Th>
                        <Th align="right">Input files</Th>
                        <Th align="right">Report files</Th>
                        <Th align="right" />
                    </tr>
                    </thead>
                    <tbody>
                    {monthsLoading && (
                        <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                                Loading…
                            </td>
                        </tr>
                    )}

                    {!monthsLoading && selectedYear && monthRows.map((m) => (
                        <MonthRow
                            key={m.month}
                            row={m}
                            onClick={() =>
                                navigate(`/commission-reports/${selectedYear}/${m.month}`)
                            }
                        />
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function buildMonthRows(year, runs) {
    if (!year) return [];
    const byMonth = new Map(runs.map((r) => [r.month, r]));
    const rows = [];
    for (let m = 1; m <= 12; m++) {
        const run = byMonth.get(m);
        rows.push({
            month: m,
            label: `${MONTH_LABELS[m - 1]} ${year}`,
            status: run?.status ?? 'NO_DATA',
            inputFileCount: run?.inputFiles?.length ?? 0,
            reportFileCount: run?.outputFiles?.length ?? 0,
        });
    }
    return rows;
}

function YearPicker({ years, loading, selectedYear, onChange }) {
    if (loading) {
        return <div className="h-9 w-32 bg-slate-100 rounded-md animate-pulse" />;
    }
    return (
        <div className="flex items-center gap-2">
            <label htmlFor="year" className="text-sm text-slate-600">Year:</label>
            <select
                id="year"
                value={selectedYear ?? ''}
                onChange={(e) => onChange(Number(e.target.value))}
                className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
                {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                ))}
            </select>
        </div>
    );
}

function Th({ children, align = 'left' }) {
    return (
        <th className={`px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider ${align === 'right' ? 'text-right' : 'text-left'}`}>
            {children}
        </th>
    );
}

function MonthRow({ row, onClick }) {
    // Every month is clickable — even NO_DATA months go to the detail page
    // so the user can upload into them.
    return (
        <tr
            onClick={onClick}
            className="border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50"
        >
            <td className="px-4 py-3 text-sm font-medium text-slate-900">{row.label}</td>
            <td className="px-4 py-3"><StatusPill status={row.status} /></td>
            <td className="px-4 py-3 text-sm text-slate-600 text-right tabular-nums">
                {row.inputFileCount}
            </td>
            <td className="px-4 py-3 text-sm text-slate-600 text-right tabular-nums">
                {row.reportFileCount}
            </td>
            <td className="px-4 py-3 text-right">
                <ChevronRight size={16} className="text-slate-400 inline" />
            </td>
        </tr>
    );
}

function StatusPill({ status }) {
    const styles = {
        COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-200',
        PENDING: 'bg-slate-50 text-slate-500 border-slate-200',
        FAILED: 'bg-red-50 text-red-700 border-red-200',
        NO_DATA: 'bg-slate-50 text-slate-400 border-slate-200',
    };
    const labels = {
        COMPLETED: 'Completed',
        IN_PROGRESS: 'In progress',
        PENDING: 'Pending',
        FAILED: 'Failed',
        NO_DATA: 'No data',
    };
    return (
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium border rounded-full ${styles[status] ?? styles.NO_DATA}`}>
            {labels[status] ?? status}
        </span>
    );
}